"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

interface Area {
  name: string;
  tier: "premier" | "standard";
  description: string;
  keyword: string;
}

const areas: Area[] = [
  { name: "Kololo", tier: "premier", description: "Kampala's most prestigious address — home to diplomats & executives. Our most requested area.", keyword: "Home nursing care Kololo Kampala" },
  { name: "Nakasero", tier: "premier", description: "Central hill residence of Uganda's business and political elite. Full service coverage.", keyword: "Elderly care at home Nakasero" },
  { name: "Naguru", tier: "premier", description: "Serene upscale neighbourhood near embassies and international schools.", keyword: "Palliative care Naguru Uganda" },
  { name: "Bugolobi", tier: "premier", description: "Modern gated community with high demand for professional home nursing.", keyword: "Wound care nursing Bugolobi Uganda" },
  { name: "Ntinda", tier: "premier", description: "Growing premium residential area with families seeking quality home care.", keyword: "Private home health nurse Ntinda" },
  { name: "Mbuya Hill", tier: "premier", description: "Elevated first-class neighbourhood with a strong expat and diplomat community.", keyword: "Bedside nurse Mbuya Hill Kampala" },
  { name: "Muyenga", tier: "standard", description: "Lake Victoria views — upper-middle residential with growing care demand.", keyword: "Bedside nurse Muyenga Kampala" },
  { name: "Munyonyo", tier: "standard", description: "Resort-adjacent luxury residential area. Home of Speke Resort.", keyword: "Medication management elderly Munyonyo" },
  { name: "Buziga", tier: "standard", description: "Hilltop residences popular with the political class and business community.", keyword: "Mobility care at home Buziga Kampala" },
  { name: "Bukoto", tier: "standard", description: "Established middle-to-upper neighbourhood near Kampala city center.", keyword: "Home health nursing Bukoto Kampala" },
  { name: "Nalya", tier: "standard", description: "Fast-growing residential area with demand for in-home nursing services.", keyword: "Personal hygiene nursing Nalya Uganda" },
  { name: "Makindye", tier: "standard", description: "Southern Kampala hill with a mix of high and middle-income families.", keyword: "Palliative care Makindye Kampala" },
  { name: "Mutungo", tier: "standard", description: "Lakeside residential area with a growing affluent community.", keyword: "Elderly nursing care Mutungo Uganda" },
  { name: "Kiwatule", tier: "standard", description: "Eastern Kampala residential suburb with increasing care service demand.", keyword: "Home nurse Kiwatule Kampala" },
  { name: "Kamwokya", tier: "standard", description: "Mixed residential area adjacent to Kololo and Naguru.", keyword: "Home nursing Kamwokya Kampala" },
  { name: "Bunga", tier: "standard", description: "Southern Kampala near Lake Victoria with upscale homes.", keyword: "Palliative nursing Bunga Kampala" },
];

export default function ServiceAreasSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            entry.target.classList.remove('hidden-init');
          }
        });
      },
      { threshold: 0.05 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-up');
    els?.forEach((el) => { el.classList.add('hidden-init'); observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const premierAreas = areas.filter(a => a.tier === 'premier');
  const standardAreas = areas.filter(a => a.tier === 'standard');

  return (
    <section
      id="areas"
      ref={sectionRef}
      className="py-24 md:py-32"
      style={{ background: 'var(--cream-warm)' }}
      aria-labelledby="areas-heading"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-up stagger-1">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase font-sans mb-4 px-4 py-2 rounded-full" style={{ color: 'var(--gold)', background: 'rgba(10,31,68,0.06)', border: '1px solid rgba(10,31,68,0.1)' }}>
            Service Areas
          </span>
          <h2 id="areas-heading" className="font-serif text-4xl md:text-5xl font-light text-navy leading-tight mt-4 mb-5">
            Serving Kampala&apos;s{" "}
            <span className="italic" style={{ color: 'var(--gold)' }}>Finest Neighbourhoods</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto leading-relaxed font-sans" style={{ color: 'var(--muted)' }}>
            From Kololo&apos;s diplomatic hills to Muyenga&apos;s lakeside estates — Beecon delivers premium home nursing care across Kampala&apos;s high-income residential areas.
          </p>
        </div>

        {/* Premier Tier */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6 reveal-up stagger-1">
            <div className="w-6 h-px" style={{ background: 'var(--gold)' }} />
            <span className="text-xs font-semibold tracking-widest uppercase font-sans" style={{ color: 'var(--gold)' }}>
              Tier 1 — Premier Coverage
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {premierAreas.map((area, i) => (
              <div
                key={`area-premier-${area.name}`}
                className={`reveal-up stagger-${(i % 6) + 1}`}
              >
                {/* SEO hidden span */}
                <span className="sr-only">{area.keyword}</span>
                <div
                  className="rounded-2xl p-5 border transition-all duration-300 cursor-default group hover:-translate-y-1 bg-[rgba(10,31,68,0.04)] border-[rgba(201,168,76,0.2)] hover:bg-[rgba(201,168,76,0.07)] hover:border-[rgba(201,168,76,0.4)]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPinIcon" size={16} variant="solid" className="flex-shrink-0" style={{ color: 'var(--gold)' }} />
                      <h3 className="font-serif text-lg font-medium text-navy">{area.name}</h3>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full font-sans" style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)' }}>
                      Premier
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed font-sans" style={{ color: 'var(--muted)' }}>
                    {area.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Standard Tier */}
        <div>
          <div className="flex items-center gap-3 mb-6 reveal-up stagger-1">
            <div className="w-6 h-px" style={{ background: 'var(--navy)' }} />
            <span className="text-xs font-semibold tracking-widest uppercase font-sans" style={{ color: 'var(--navy)' }}>
              Tier 2 — Full Coverage
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {standardAreas.map((area, i) => (
              <div
                key={`area-standard-${area.name}`}
                className={`reveal-up stagger-${(i % 5) + 1}`}
              >
                <span className="sr-only">{area.keyword}</span>
                <div
                  className="rounded-xl p-4 border transition-all duration-300 cursor-default hover:-translate-y-1 group bg-white border-[rgba(10,31,68,0.08)] shadow-[0_1px_4px_rgba(10,31,68,0.04)] hover:border-[rgba(201,168,76,0.3)] hover:shadow-[0_6px_20px_rgba(201,168,76,0.1)]"
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon name="MapPinIcon" size={13} variant="solid" style={{ color: 'var(--navy)', opacity: 0.5 }} />
                    <h3 className="font-serif text-sm font-medium text-navy">{area.name}</h3>
                  </div>
                  <p className="text-xs leading-relaxed font-sans" style={{ color: 'var(--muted)' }}>
                    {area.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Not listed CTA */}
        <div className="mt-12 text-center reveal-up stagger-3">
          <p className="text-sm font-sans mb-4" style={{ color: 'var(--muted)' }}>
            Don&apos;t see your neighbourhood? We may still cover your area.
          </p>
          <a
            href="https://wa.me/256788217719"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-gold inline-flex items-center gap-2 px-6 py-3 text-sm"
            style={{ borderColor: 'rgba(10,31,68,0.2)', color: 'var(--navy)' }}
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} style={{ color: 'var(--navy)' }} />
            Ask About Your Area
          </a>
        </div>
      </div>
    </section>
  );
}